import axios from 'axios';

import type { UploadFn, UploadedFile } from './types';

export type PresignResult = {
  /** The storage URL (S3/Azure) to PUT the raw bytes to. */
  uploadUrl: string;
  /** The ref to commit to the form once the PUT succeeds. */
  file: UploadedFile;
  /** Provider-specific headers required by the presigned URL. */
  putHeaders?: Record<string, string>;
};

/**
 * Build an UploadFn that performs the standard presign -> direct PUT flow.
 * Storage URLs are off-origin, so this uses a bare axios.put (not the API
 * interceptor client). Provider-specific concerns live in getPresignedUrl.
 */
export function createPresignedUploader(options: {
  getPresignedUrl: (file: File) => Promise<PresignResult>;
}): UploadFn {
  return async (file, { onProgress, signal }) => {
    const { uploadUrl, file: stored, putHeaders } = await options.getPresignedUrl(file);
    await axios.put(uploadUrl, file, {
      signal,
      headers: { 'Content-Type': file.type, ...putHeaders },
      onUploadProgress: (event) => {
        if (event.total) onProgress(Math.round((event.loaded / event.total) * 100));
      },
    });
    return stored;
  };
}
